/**
 * API Service para comunicação com o backend
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache para evitar requisições repetidas
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

const getCached = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  return null;
};

const setCache = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

/**
 * Fetch com fallback para mock data
 */
const fetchWithFallback = async (endpoint, fallbackData) => {
  // Check cache first
  const cached = getCached(endpoint);
  if (cached) return cached;

  try {
    const response = await fetch(`${API_URL}${endpoint}`);
    if (!response.ok) throw new Error('API error');
    const data = await response.json();
    setCache(endpoint, data);
    return data;
  } catch (error) {
    console.warn(`API fallback for ${endpoint}:`, error.message);
    return fallbackData;
  }
};

// ============ PUBLIC API FUNCTIONS ============

export const api = {
  /**
   * Get all projects
   */
  getProjects: async (fallback = []) => {
    return fetchWithFallback('/api/public/projects', fallback);
  },

  /**
   * Get all blog posts
   */
  getBlogPosts: async (fallback = []) => {
    return fetchWithFallback('/api/public/blog', fallback);
  },

  /**
   * Get blog post by slug
   */
  getBlogPostBySlug: async (slug, fallback = null) => {
    return fetchWithFallback(`/api/public/blog/${slug}`, fallback);
  },

  /**
   * Get all testimonials
   */
  getTestimonials: async (fallback = []) => {
    return fetchWithFallback('/api/public/testimonials', fallback);
  },

  /**
   * Get all FAQs
   */
  getFAQs: async (fallback = []) => {
    return fetchWithFallback('/api/public/faqs', fallback);
  },

  /**
   * Get all gallery items
   */
  getGallery: async (fallback = []) => {
    return fetchWithFallback('/api/public/gallery', fallback);
  },

  /**
   * Clear cache (useful after admin updates)
   */
  clearCache: () => {
    cache.clear();
  }
};

export default api;
